import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Remove local admin, keep people working',
    description:
      'Elevation follows the application and the action, not the user. Nobody carries standing admin rights; the task that needs them gets them, briefly.',
    bullets: [
      'Just-in-time, time-bound elevation per application',
      'Justification and approval flows for sensitive elevations',
      'No standing local-admin membership on endpoints',
      'Users keep working: no ticket, no wait, no workaround',
    ],
  },
  {
    title: 'Control what runs',
    description:
      'A Windows kernel-level agent intercepts process creation and applies your policy before anything executes.',
    bullets: [
      'Allow / block by hash, signature, and publisher',
      'Kernel-mode interception, not a wrapper script',
      'Policy decisions logged with full context',
      'Block-first posture for unknown binaries',
    ],
  },
  {
    title: 'Built for on-prem and air-gap',
    description:
      'Policies live in an encrypted offline cache on the endpoint, so enforcement holds on planes, in plants, and behind air gaps.',
    bullets: [
      'Encrypted offline policy cache per endpoint',
      'Enforcement continues without connectivity',
      'Self-hosted control plane, same Monopam console',
      'No cloud dependency for policy decisions',
    ],
  },
  {
    title: 'Local accounts, vaulted and rotated',
    description:
      'The local admin accounts that remain go under Monopam management: rotated like every other privileged credential, with a full audit chain.',
    bullets: [
      'LAPS-style local admin credential rotation',
      'Local accounts vaulted alongside servers and databases',
      'Every elevation and execution decision audited',
      'One privileged-access console: endpoints to clusters',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Deploy the agent',
    description:
      'Roll the Windows agent out to endpoints; it reports inventory and starts enforcing in audit-first mode.',
  },
  {
    title: 'Shape the policy',
    description:
      'Allow the known, block the bad, and route the gray area to elevation with justification and approval.',
  },
  {
    title: 'Drop the admin rights',
    description:
      'Remove standing local admin, vault and rotate what remains, and watch the endpoint attack surface shrink.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Credential vault',
    description:
      'Where the remaining local admin accounts live and rotate.',
    href: '/pam/vault',
  },
  {
    label: 'Just-in-time access',
    description:
      'The same time-bound model, applied to servers and clouds.',
    href: '/pam/jit-access',
  },
  {
    label: 'What is EPM?',
    description:
      'Endpoint privilege management, explained in the glossary.',
    href: '/glossary/endpoint-privilege-management',
  },
]

export default function EpmPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Endpoint Privilege Management (EPM)"
        description="Endpoint privilege management inside your PAM: just-in-time elevation with approval, allow/block by hash, signature, and publisher, encrypted offline policy cache for air-gapped estates, and LAPS-style local admin rotation."
        path="/pam/epm"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Endpoint Privilege Management"
          title={
            <>
              Every laptop is a
              <br />
              privileged system. Manage it.
            </>
          }
          body="Local admin rights are the most widely distributed privilege in your company. Monopam EPM removes them without stopping work: just-in-time elevation per application, allow/block control over what runs, and offline-safe policy for the field."
          badges={[
            'JIT elevation, no standing admin',
            'Allow / block by publisher & hash',
            'On-prem & air-gap friendly',
          ]}
        />
        <SolutionPillars
          title={<>Least privilege, extended to the endpoint.</>}
          body="The same platform that brokers your servers, databases, and clusters governs what runs (and what elevates) on the machines your people use every day."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From admin-everywhere to least privilege.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
